import { createContext, ReactNode, useCallback, useContext, useState } from "react";

interface ConfirmOpts {
  /** Big heading at the top of the modal. */
  title: string;
  /** Optional body paragraph. */
  body?: ReactNode;
  /** Primary button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Render the primary button in red for destructive actions. */
  danger?: boolean;
  /** Render the primary button in orange/warning for cautious actions. */
  warning?: boolean;
  /** Hide the cancel button. Used for plain alerts. */
  alertOnly?: boolean;
  /** If set, renders a text input + label; the value can be read via usePrompt. */
  prompt?: {
    label: string;
    placeholder?: string;
    initial?: string;
    required?: boolean;
    multiline?: boolean;
  };
}

export interface PromptResult { ok: boolean; value: string }

interface Pending {
  opts: ConfirmOpts;
  resolve: (result: boolean | PromptResult) => void;
}

const Ctx = createContext<((opts: ConfirmOpts) => Promise<any>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback((opts: ConfirmOpts) => {
    return new Promise<boolean | PromptResult>((resolve) => setPending({ opts, resolve }));
  }, []);

  function close(result: boolean | PromptResult) {
    if (!pending) return;
    pending.resolve(result);
    setPending(null);
  }

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {pending && <Modal opts={pending.opts} onClose={close} />}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx as (opts: ConfirmOpts) => Promise<boolean>;
}

/** Convenience: alert-style (no cancel). */
export function useAlert() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAlert must be used inside <ConfirmProvider>");
  return (opts: Omit<ConfirmOpts, "alertOnly">) => c({ ...opts, alertOnly: true }) as Promise<boolean>;
}

/** Prompt-style: returns { ok, value }. */
export function usePrompt() {
  const c = useContext(Ctx);
  if (!c) throw new Error("usePrompt must be used inside <ConfirmProvider>");
  return (opts: ConfirmOpts & { prompt: NonNullable<ConfirmOpts["prompt"]> }) =>
    c(opts) as Promise<PromptResult>;
}

function Modal({ opts, onClose }: { opts: ConfirmOpts; onClose: (result: boolean | PromptResult) => void }) {
  const accent = opts.danger
    ? { ring: "bg-liaise-red/15 text-liaise-red", btn: "bg-liaise-red text-white hover:opacity-90" }
    : opts.warning
      ? { ring: "bg-liaise-orange/20 text-liaise-orange", btn: "bg-liaise-orange text-white hover:opacity-90" }
      : { ring: "bg-liaise-blue/15 text-liaise-blue", btn: "bg-liaise-navy text-white hover:opacity-90" };
  const [value, setValue] = useState(opts.prompt?.initial ?? "");

  function confirm() {
    if (opts.prompt) {
      const v = value.trim();
      if (opts.prompt.required && !v) return;
      onClose({ ok: true, value: v });
    } else {
      onClose(true);
    }
  }
  function cancel() {
    if (opts.prompt) onClose({ ok: false, value: "" });
    else onClose(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={cancel}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${accent.ring}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-liaise-navy">{opts.title}</h3>
            {opts.body && <div className="text-sm text-slate-600 mt-2 leading-relaxed">{opts.body}</div>}
            {opts.prompt && (
              <label className="block mt-4">
                <div className="text-xs font-medium text-slate-500 mb-1">{opts.prompt.label}{opts.prompt.required && <span className="text-red-500"> *</span>}</div>
                {opts.prompt.multiline ? (
                  <textarea value={value} placeholder={opts.prompt.placeholder} rows={3}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm" autoFocus />
                ) : (
                  <input type="text" value={value} placeholder={opts.prompt.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") confirm(); }}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm" autoFocus />
                )}
              </label>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          {!opts.alertOnly && (
            <button onClick={cancel}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium hover:bg-slate-50">
              {opts.cancelLabel ?? "Cancel"}
            </button>
          )}
          <button onClick={confirm} className={`px-4 py-2 rounded-lg text-sm font-medium ${accent.btn}`} autoFocus={!opts.prompt}>
            {opts.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
